/**
 * `withTimeout` — teto de espera para qualquer promessa do boot.
 *
 * ── Por que isto existe ─────────────────────────────────────────────────────
 * O boot do app é uma fila de `await`s (resultado de redirect, estado de auth,
 * perfil, veredito do beta) e **nenhum deles tinha teto**. Quando um pendura,
 * o `isLoading` do AuthContext nunca sai de `true` e a pessoa fica olhando um
 * spinner para sempre — sem erro, sem log, sem nada a reportar. Foi o sintoma
 * do build 22 no TestFlight.
 *
 * "Pendurar" não é hipótese remota aqui: o SDK do Firebase Auth espera um
 * iframe de rede antes de disparar o primeiro `onAuthStateChanged`, e o
 * Firestore é offline-first por desenho — um `getDoc` sem conexão não rejeita,
 * ele **espera**, que é o comportamento certo dentro do app e o comportamento
 * errado no caminho crítico da primeira tela.
 *
 * ── O que este helper NÃO faz ───────────────────────────────────────────────
 * Não cancela a operação original: JavaScript não tem cancelamento de promessa,
 * e fingir que tem seria pior. A promessa perdedora continua correndo e seu
 * resultado é descartado. Isso é aceitável porque o boot é idempotente — a
 * tentativa seguinte refaz o passo do zero.
 */

/** Teto padrão de qualquer etapa do boot. */
export const BOOT_TIMEOUT_MS = 10_000;

/**
 * Erro de etapa estourada. Carrega o `code` que a tela de falha mostra à
 * pessoa e que ela consegue reportar por print — é o único canal de
 * diagnóstico que temos sem Safari Web Inspector.
 */
export class BootTimeoutError extends Error {
  readonly code: string;
  readonly ms: number;

  constructor(code: string, ms: number) {
    super(`${code} (${ms}ms)`);
    this.name = "BootTimeoutError";
    this.code = code;
    this.ms = ms;
  }
}

export function isBootTimeout(e: unknown): e is BootTimeoutError {
  return e instanceof BootTimeoutError;
}

/**
 * Resolve com o valor de `promise`, ou rejeita com `BootTimeoutError` depois de
 * `ms`. O timer é sempre limpo — um `setTimeout` pendurado por etapa manteria o
 * event loop acordado e, em iOS, atrasa a suspensão do app.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  code: string,
  ms: number = BOOT_TIMEOUT_MS,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new BootTimeoutError(code, ms)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Versão para APIs de CALLBACK, que é o caso do `onAuthStateChanged`: ele não
 * devolve promessa, e o que precisamos vigiar é "o primeiro disparo chegou?".
 *
 * Devolve `{ armed, disarm }`: quem receber o callback chama `disarm()`. Se o
 * disparo não vier dentro de `ms`, `armed` rejeita com o código.
 */
export function armTimeout(
  code: string,
  ms: number = BOOT_TIMEOUT_MS,
): { armed: Promise<never>; disarm: () => void } {
  let disarm = () => {};
  const armed = new Promise<never>((_, reject) => {
    const timer = setTimeout(() => reject(new BootTimeoutError(code, ms)), ms);
    disarm = () => clearTimeout(timer);
  });
  // Sem isto, um `disarm()` que chega antes de alguém dar `.catch` em `armed`
  // deixa uma rejeição não tratada no console do device — ruído justamente na
  // superfície que estamos usando para diagnosticar.
  armed.catch(() => {});
  return { armed, disarm };
}
